import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Search, X } from 'lucide-react';
import { createBlog, getAllBlogs, updateBlog, deleteBlog, BlogPost as ApiBlogPost } from '../../services/blog';
import { getAllBlogCategories, createBlogCategory, updateBlogCategory, deleteBlogCategory } from '../../services/blogCategory';
import Loader from '../../components/Loader';
import RichTextEditor from '../../components/RichTextEditor';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publishedDate: string;
  category: string | { _id: string; name: string; slug: string };
  slug: string;
  status: 'draft' | 'published';
  image: string;
}

const AdminBlogPage = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [newPostImage, setNewPostImage] = useState<string>('');
  const [editingPostImage, setEditingPostImage] = useState<string>('');
  const [newPostContent, setNewPostContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPost, setDeletingPost] = useState<BlogPost | null>(null);
  const [categories, setCategories] = useState([
    { id: 'all', name: 'All Categories', description: '' }
  ]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<any>(null);

  // Load blog posts and categories from API
  useEffect(() => {
    loadBlogPosts();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await getAllBlogCategories();
      console.log('Blog categories response:', response);
      
      if (response.success && response.data) {
        const apiCategories = response.data.map((cat: any) => ({
          id: cat._id || cat.id,
          name: cat.name,
          description: cat.description || ''
        }));
        setCategories([{ id: 'all', name: 'All Categories', description: '' }, ...apiCategories]);
      } else {
        // If no categories from API, start with just "All Categories"
        setCategories([{ id: 'all', name: 'All Categories', description: '' }]);
      }
    } catch (error) {
      console.error('Error loading blog categories:', error);
      // Start with just "All Categories" if API fails
      setCategories([{ id: 'all', name: 'All Categories', description: '' }]);
    }
  };

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getAllBlogs();
      if (response.success && Array.isArray(response.data)) {
        setBlogPosts(response.data.map(apiPost => ({
          id: apiPost._id || '',
          title: apiPost.title,
          excerpt: apiPost.excerpt,
          content: apiPost.content,
          author: apiPost.author,
          publishedDate: apiPost.publishedDate,
          category: typeof apiPost.category === 'object' ? apiPost.category.name : apiPost.category,
          slug: apiPost.slug,
          status: apiPost.status,
          image: apiPost.image || ''
        })));
      }
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load blog posts');
      // Fallback to mock data if API fails
      setBlogPosts(mockBlogPosts);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for fallback
  const mockBlogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'Top 10 Tips for Agricultural Entrance Exam Preparation',
      excerpt: 'Discover the most effective strategies to prepare for agricultural entrance exams and secure your spot in top institutions.',
      content: 'Full content here...',
      author: 'Dr. Sarah Johnson',
      publishedDate: '2024-01-15',
      category: 'Education',
      slug: 'top-10-tips-agricultural-entrance-exam',
      status: 'published',
      image: '/api/placeholder/400/250'
    },
    {
      id: '2',
      title: 'Understanding Soil Science: A Complete Guide',
      excerpt: 'Master the fundamentals of soil science with our comprehensive guide covering soil types, composition, and management.',
      content: 'Full content here...',
      author: 'Prof. Michael Chen',
      publishedDate: '2024-01-10',
      category: 'Science',
      slug: 'understanding-soil-science-complete-guide',
      status: 'published',
      image: '/api/placeholder/400/250'
    },
    {
      id: '3',
      title: 'Modern Farming Techniques for Sustainable Agriculture',
      excerpt: 'Explore innovative farming methods that promote sustainability while maximizing crop yields and environmental protection.',
      content: 'Full content here...',
      author: 'Dr. Emily Rodriguez',
      publishedDate: '2024-01-05',
      category: 'Agriculture',
      slug: 'modern-farming-techniques-sustainable-agriculture',
      status: 'draft',
      image: '/api/placeholder/400/250'
    }
  ];

  const statuses = [
    { id: 'all', name: 'All Status' },
    { id: 'published', name: 'Published' },
    { id: 'draft', name: 'Draft' }
  ];

  // Add category handler
  const handleAddCategory = async () => {
    const trimmed = newCategory.trim();
    const description = newCategoryDescription.trim();
    if (!trimmed) return;
    if (categories.some(cat => cat.name.toLowerCase() === trimmed.toLowerCase())) return;
    
    try {
      const response = await createBlogCategory({
        name: trimmed,
        description: description || trimmed
      });
      
      if (response.success) {
        // Reload categories from API to get the proper ID
        await loadCategories();
        setNewCategory('');
        setNewCategoryDescription('');
      }
    } catch (error) {
      console.error('Error creating blog category:', error);
      alert('Failed to create blog category');
    }
  };

  // Edit category handler
  const handleEditCategory = async () => {
    if (!editingCategory) return;
    
    try {
      const response = await updateBlogCategory(editingCategory.id, {
        name: editingCategory.name,
        description: editingCategory.description
      });
      
      if (response.success) {
        // Reload categories from API to ensure consistency
        await loadCategories();
        setShowEditCategoryModal(false);
        setEditingCategory(null);
      }
    } catch (error) {
      console.error('Error updating blog category:', error);
      alert('Failed to update blog category');
    }
  };

  // Delete category handler
  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    
    try {
      const response = await deleteBlogCategory(deletingCategory.id);
      
      if (response.success) {
        // Reload categories from API to ensure consistency
        await loadCategories();
        setShowDeleteCategoryModal(false);
        setDeletingCategory(null);
      }
    } catch (error) {
      console.error('Error deleting blog category:', error);
      alert('Failed to delete blog category');
    }
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || post.status === selectedStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleDeleteClick = (post: BlogPost) => {
    setDeletingPost(post);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPost) return;
    
    try {
      setLoading(true);
      const response = await deleteBlog(deletingPost.id);
      if (response.success) {
        setBlogPosts(blogPosts.filter(post => post.id !== deletingPost.id));
        setShowDeleteModal(false);
        setDeletingPost(null);
      } else {
        alert('Failed to delete blog post');
      }
    } catch (err) {
      console.error('Error deleting blog post:', err);
      alert('Failed to delete blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingPost(null);
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setEditingPostImage(post.image);
    setShowEditForm(true);
  };

  const handleSaveEdit = async () => {
    if (editingPost) {
      try {
        setLoading(true);
        const updateData = {
          title: editingPost.title,
          excerpt: editingPost.excerpt,
          content: editingPost.content,
          author: editingPost.author,
          category: editingPost.category,
          status: editingPost.status,
          image: editingPostImage || editingPost.image
        };
        
        const response = await updateBlog(editingPost.id, updateData);
        if (response.success) {
          const updatedPost = {
            ...editingPost,
            image: editingPostImage || editingPost.image
          };
          setBlogPosts(blogPosts.map(post => 
            post.id === editingPost.id ? updatedPost : post
          ));
          setShowEditForm(false);
          setEditingPost(null);
          setEditingPostImage('');
        } else {
          alert('Failed to update blog post');
        }
      } catch (err) {
        console.error('Error updating blog post:', err);
        alert('Failed to update blog post');
      } finally {
        setLoading(false);
      }
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const base64 = await convertToBase64(file);
        if (isEdit) {
          setEditingPostImage(base64);
        } else {
          setNewPostImage(base64);
        }
      } catch (error) {
        console.error('Error converting image to base64:', error);
        alert('Error uploading image. Please try again.');
      }
    }
  };

  const handleCreatePost = async () => {
    try {
      setLoading(true);
      
      // Get form values using more specific selectors
      const form = document.querySelector('form') as HTMLFormElement;
      const title = (form.querySelector('input[placeholder="Enter post title..."]') as HTMLInputElement)?.value || '';
      const excerpt = (form.querySelector('textarea[placeholder="Enter a brief excerpt..."]') as HTMLTextAreaElement)?.value || '';
      const content = newPostContent || '';
      const author = (form.querySelector('input[placeholder="Enter author name..."]') as HTMLInputElement)?.value || '';
      
      // Get category from the first select in the form (not the filter)
      const categorySelect = form.querySelector('select') as HTMLSelectElement;
      const category = categorySelect?.value || '';
      
      // Get status from the second select in the form
      const statusSelect = form.querySelectorAll('select')[1] as HTMLSelectElement;
      const status = statusSelect?.value as 'draft' | 'published' || 'draft';

      // Validate required fields
      if (!title.trim()) {
        alert('Please enter a title');
        return;
      }
      if (!excerpt.trim()) {
        alert('Please enter an excerpt');
        return;
      }
      if (!content.trim()) {
        alert('Please enter content');
        return;
      }
      if (!author.trim()) {
        alert('Please enter an author');
        return;
      }
      if (!category || category === 'all') {
        alert('Please select a valid category');
        return;
      }
      if (!status) {
        alert('Please select a valid status');
        return;
      }

      // Generate slug from title
      const slug = title.trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const blogData = {
        title: title.trim(),
        excerpt: excerpt.trim(),
        content: content.trim(),
        author: author.trim(),
        category,
        status,
        slug,
        image: newPostImage
      };

      console.log('Sending blog data:', blogData);
      const response = await createBlog(blogData);
      if (response.success && response.data) {
        const newPost: BlogPost = {
          id: (response.data as ApiBlogPost)._id || '',
          title: (response.data as ApiBlogPost).title,
          excerpt: (response.data as ApiBlogPost).excerpt,
          content: (response.data as ApiBlogPost).content,
          author: (response.data as ApiBlogPost).author,
          publishedDate: (response.data as ApiBlogPost).publishedDate,
          category: (response.data as ApiBlogPost).category,
          slug: (response.data as ApiBlogPost).slug,
          status: (response.data as ApiBlogPost).status,
          image: (response.data as ApiBlogPost).image || ''
        };

        setBlogPosts([...blogPosts, newPost]);
        setShowCreateForm(false);
        setNewPostImage('');
        setNewPostContent('');
      } else {
        alert('Failed to create blog post');
      }
    } catch (err) {
      console.error('Error creating blog post:', err);
      alert('Failed to create blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6">
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Blog Management</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Create, edit, and manage blog posts for your agricultural content
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-[24px] hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
            aria-label="Manage Categories"
          >
            Manage Categories
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-green-800 hover:bg-green-900 disabled:bg-gray-400 text-white rounded-[24px] transition-colors duration-200"
            aria-label="Create New Post"
          >
            <Plus className="h-4 w-4 mr-2" />
            {loading ? (
              <Loader variant="inline" size="sm" text="Loading..." />
            ) : (
              'Create New Post'
            )}
          </button>
        </div>
      </div>
      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-lg p-6 w-full max-w-2xl relative">
            <button
              onClick={() => setShowCategoryModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Manage Categories</h2>
            
            {/* Categories List */}
            <div className="mb-6">
              <h3 className="text-md font-medium mb-3 text-gray-900 dark:text-white">Existing Categories</h3>
              <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-[24px]">
                {categories.filter(cat => cat.id !== 'all').map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-600 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">{cat.name}</div>
                      {cat.description && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{cat.description}</div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setEditingCategory(cat);
                          setShowEditCategoryModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit Category"
                        aria-label="Edit Category"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingCategory(cat);
                          setShowDeleteCategoryModal(true);
                        }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete Category"
                        aria-label="Delete Category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {categories.filter(cat => cat.id !== 'all').length === 0 && (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No categories found. Create your first category below.
                  </div>
                )}
              </div>
            </div>

            {/* Add New Category */}
            <div>
              <h3 className="text-md font-medium mb-3 text-gray-900 dark:text-white">Add New Category</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Category name..."
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                />
                <textarea
                  value={newCategoryDescription}
                  onChange={e => setNewCategoryDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Category description (optional)..."
                  rows={2}
                />
                <button
                  onClick={handleAddCategory}
                  className="w-full px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
                  aria-label="Add Category"
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditCategoryModal && editingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-lg p-6 w-full max-w-md relative">
            <button
              onClick={() => {
                setShowEditCategoryModal(false);
                setEditingCategory(null);
              }}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Edit Category</h2>
            <div className="space-y-3">
              <input
                type="text"
                value={editingCategory.name}
                onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Category name..."
              />
              <textarea
                value={editingCategory.description || ''}
                onChange={e => setEditingCategory({ ...editingCategory, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Category description..."
                rows={3}
              />
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowEditCategoryModal(false);
                    setEditingCategory(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditCategory}
                  className="flex-1 px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
                  aria-label="Save Changes"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {showDeleteCategoryModal && deletingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-lg p-6 w-full max-w-md relative">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Category
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Are you sure you want to delete this category? This action cannot be undone.
              </p>
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-[24px]">
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  {deletingCategory.name}
                </p>
                {deletingCategory.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {deletingCategory.description}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteCategoryModal(false);
                  setDeletingCategory(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCategory}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-[24px] transition-colors duration-200"
                aria-label="Delete Category"
              >
                Delete Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setSelectedStatus('all');
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
            aria-label="Clear Filters"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Loading State for Initial Load */}
      {loading && blogPosts.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-12">
          <Loader variant="fullscreen" text="Loading blog posts..." />
        </div>
      )}

      {/* Blog Posts Table */}
      {!loading || blogPosts.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Post
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPosts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {post.title}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{post.author}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {typeof post.category === 'object' ? post.category.name : post.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      post.status === 'published' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(post.publishedDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/blog/${post.slug}`)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                        title="View Post"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(post)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit Post"
                        aria-label="Edit Post"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(post)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete Post"
                        aria-label="Delete Post"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No blog posts found matching your criteria.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
              aria-label="Create Your First Post"
            >
              Create Your First Post
            </button>
          </div>
        )}
      </div>
      ) : null}

             {/* Create Blog Post Form Modal */}
       {showCreateForm && (
         <div className="fixed inset-0 bg-white dark:bg-gray-800 z-50 overflow-y-auto">
           <div className="min-h-screen">
                         <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
               <div className="max-w-4xl mx-auto p-6 flex items-center justify-between">
                 <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                   Create New Blog Post
                 </h2>
                 <div className="flex items-center space-x-3">
                   <button
                     onClick={() => setShowCreateForm(false)}
                     className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                     aria-label="Cancel"
                   >
                     Cancel
                   </button>
                   <button
                     onClick={handleCreatePost}
                     disabled={loading}
                     className="px-4 py-2 bg-green-800 hover:bg-green-900 disabled:bg-gray-400 text-white rounded-[24px] transition-colors duration-200"
                     aria-label="Create Post"
                   >
                     {loading ? (
                       <Loader variant="inline" size="sm" text="Creating..." />
                     ) : (
                       'Create Post'
                     )}
                   </button>
                 </div>
               </div>
             </div>
            
                         <div className="p-6 max-w-4xl mx-auto">
               <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleCreatePost(); }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter post title..."
                  />
                </div>



               <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Featured Image
                   </label>
                   <div className="flex items-center justify-center h-10 px-3 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-[24px] hover:border-green-400 dark:hover:border-green-500 transition-colors duration-200 bg-gray-50 dark:bg-gray-800">
                     {newPostImage ? (
                       <div className="flex items-center space-x-2">
                         <img 
                           src={newPostImage} 
                           alt="Blog post preview image" 
                           className="h-8 w-8 rounded object-cover"
                         />
                         <button
                           type="button"
                           onClick={() => setNewPostImage('')}
                           className="text-xs text-red-600 hover:text-red-500"
                           aria-label="Remove"
                         >
                           Remove
                         </button>
                       </div>
                     ) : (
                       <div className="flex items-center space-x-2">
                       <svg
                         className="h-5 w-5 text-gray-400"
                         stroke="currentColor"
                         fill="none"
                         viewBox="0 0 48 48"
                         aria-hidden="true"
                       >
                         <path
                           d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                           strokeWidth={2}
                           strokeLinecap="round"
                           strokeLinejoin="round"
                         />
                       </svg>
                       <label
                         htmlFor="file-upload"
                         className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-green-600 dark:hover:text-green-400"
                         aria-label="Upload"
                       >
                         Upload
                         <input 
                           id="file-upload" 
                           name="file-upload" 
                           type="file" 
                           className="sr-only" 
                           accept="image/*"
                           onChange={(e) => handleImageUpload(e, false)}
                         />
                       </label>
                     </div>
                     )}
                   </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Category
                   </label>
                   <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500">
                     <option value="">Select category...</option>
                     {categories.slice(1).map((category) => (
                       <option key={category.id} value={category.id}>
                         {category.name}
                       </option>
                     ))}
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Author
                   </label>
                   <input
                     type="text"
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                     placeholder="Enter author name..."
                   />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Status
                   </label>
                   <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500">
                     <option value="draft">Draft</option>
                     <option value="published">Published</option>
                   </select>
                 </div>
               </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Excerpt
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter a brief excerpt..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content
                  </label>
                  <RichTextEditor
                    value={newPostContent}
                    onChange={setNewPostContent}
                    placeholder="Write your blog post content..."
                  />
                </div>


              </form>
            </div>
          </div>
                 </div>
       )}

       {/* Edit Blog Post Form Modal */}
       {showEditForm && editingPost && (
         <div className="fixed inset-0 bg-white dark:bg-gray-800 z-50 overflow-y-auto">
           <div className="min-h-screen">
                            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                 <div className="max-w-4xl mx-auto p-6 flex items-center justify-between">
                   <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                     Edit Blog Post
                   </h2>
                   <div className="flex items-center space-x-3">
                     <button
                       onClick={() => {
                         setShowEditForm(false);
                         setEditingPost(null);
                       }}
                       className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                       aria-label="Cancel"
                     >
                       Cancel
                     </button>
                     <button
                       onClick={handleSaveEdit}
                       disabled={loading}
                       className="px-4 py-2 bg-green-800 hover:bg-green-900 disabled:bg-gray-400 text-white rounded-[24px] transition-colors duration-200"
                       aria-label="Save Changes"
                      >
                       {loading ? (
                         <Loader variant="inline" size="sm" text="Saving..." />
                       ) : (
                         'Save Changes'
                       )}
                     </button>
                   </div>
                 </div>
               </div>
             
             <div className="p-6 max-w-4xl mx-auto">
               <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Title
                   </label>
                   <input
                     type="text"
                     value={editingPost.title}
                     onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                     placeholder="Enter post title..."
                   />
                 </div>



                                   <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Featured Image
                     </label>
                     <div className="flex items-center justify-center h-10 px-3 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-[24px] hover:border-green-400 dark:hover:border-green-500 transition-colors duration-200 bg-gray-50 dark:bg-gray-800">
                       {editingPostImage ? (
                         <div className="flex items-center space-x-2">
                           <img 
                             src={editingPostImage} 
                             alt="Blog post featured image" 
                             className="h-8 w-8 rounded object-cover"
                           />
                           <button
                             type="button"
                             onClick={() => setEditingPostImage('')}
                             className="text-xs text-red-600 hover:text-red-500"
                             aria-label="Remove"
                           >
                             Remove
                           </button>
                         </div>
                       ) : (
                         <div className="flex items-center space-x-2">
                           <svg
                             className="h-5 w-5 text-gray-400"
                             stroke="currentColor"
                             fill="none"
                             viewBox="0 0 48 48"
                             aria-hidden="true"
                           >
                             <path
                               d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                               strokeWidth={2}
                               strokeLinecap="round"
                               strokeLinejoin="round"
                             />
                           </svg>
                           <label
                             htmlFor="edit-file-upload"
                             className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-green-600 dark:hover:text-green-400"
                             aria-label="Upload"
                           >
                             Upload
                             <input 
                               id="edit-file-upload" 
                               name="edit-file-upload" 
                               type="file" 
                               className="sr-only" 
                               accept="image/*"
                               onChange={(e) => handleImageUpload(e, true)}
                             />
                           </label>
                         </div>
                       )}
                     </div>
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Category
                     </label>
                     <select 
                       value={typeof editingPost.category === 'object' ? editingPost.category._id : editingPost.category}
                       onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                     >
                       {categories.slice(1).map((category) => (
                         <option key={category.id} value={category.id}>
                           {category.name}
                         </option>
                       ))}
                     </select>
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Author
                     </label>
                     <input
                       type="text"
                       value={editingPost.author}
                       onChange={(e) => setEditingPost({ ...editingPost, author: e.target.value })}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                       placeholder="Enter author name..."
                     />
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Status
                     </label>
                     <select 
                       value={editingPost.status}
                       onChange={(e) => setEditingPost({ ...editingPost, status: e.target.value as 'draft' | 'published' })}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                     >
                       <option value="draft">Draft</option>
                       <option value="published">Published</option>
                     </select>
                   </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Excerpt
                   </label>
                   <textarea
                     rows={3}
                     value={editingPost.excerpt}
                     onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                     placeholder="Enter a brief excerpt..."
                   />
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Content
                   </label>
                   <RichTextEditor
                     value={editingPost.content}
                     onChange={(content) => setEditingPost({ ...editingPost, content })}
                     placeholder="Write your blog post content..."
                   />
                 </div>


               </form>
             </div>
           </div>
         </div>
       )}

       {/* Delete Confirmation Modal */}
       {showDeleteModal && deletingPost && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white dark:bg-gray-800 rounded-[24px] p-6 max-w-md w-full mx-4">
             <div className="flex items-center mb-4">
               <div className="flex-shrink-0">
                 <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                   <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                 </div>
               </div>
               <div className="ml-3">
                 <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                   Delete Blog Post
                 </h3>
               </div>
             </div>
             
             <div className="mb-6">
               <p className="text-sm text-gray-600 dark:text-gray-300">
                 Are you sure you want to delete this blog post? This action cannot be undone.
               </p>
               <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-[24px]">
                 <p className="font-medium text-gray-900 dark:text-white text-sm">
                   {deletingPost.title}
                 </p>
                 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                   {deletingPost.author} • {formatDate(deletingPost.publishedDate)}
                 </p>
               </div>
             </div>
             
             <div className="flex justify-end space-x-3">
               <button
                 type="button"
                 onClick={handleDeleteCancel}
                 disabled={loading}
                 className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
                 aria-label="Cancel"
               >
                 Cancel
               </button>
                                <button
                   type="button"
                   onClick={handleDeleteConfirm}
                   disabled={loading}
                   className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-[24px] transition-colors duration-200"
                   aria-label="Delete Post"
                 >
                   {loading ? (
                     <Loader variant="inline" size="sm" text="Deleting..." />
                   ) : (
                     'Delete Post'
                   )}
                 </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default AdminBlogPage; 